import {
  CloudAccountApiReadScanningConfigRequest,
  OnboardingConfigResponse,
} from '@ariksa/cloud-account/api';
import {
  FileDataApiGetScannersRequest,
  ScannerStatusResponse,
} from '@ariksa/data-scanning/api';
import {
  InsightV2ApiInsightV2Request,
  InsightV2Pagination,
  ResourceApiGetZonesByRegionForAccountRequest,
} from '@ariksa/inventory-core/api';
import { forEach } from 'lodash';
import { QueryAction, QueryStatus } from 'services/types';
import { QueryState } from 'services/utils/QueryState';
import {
  DefaultApiAddEnabledRegionsRequest,
  DefaultApiGetCrossAccountsForScanningRequest,
  DefaultApiGetEnabledRegionsRequest,
  DefaultApiReplaceEnabledRegionsRequest,
  DefaultApiUpdateScannerSettingsRequest,
  ItemApiUpdateCentralScanningAccountStatusRequest,
  OrgEnabledRegionsResponse,
} from 'services/Vulnerability/legacyRequestTypes';

import {
  ScanVaultCentralAccount,
  ScanVaultCrossAccountsResponse,
  ScanVaultScannerInfo,
} from 'api/scan-vault/client';
import { createSlice } from 'utils/@reduxjs/toolkit';

import { ContainerState } from './types';

export const initialState: ContainerState = {
  ariksaOutposts: QueryState.init([]),
  dataOutposts: QueryState.init({}),
  vulnerabilityOutposts: QueryState.init([]),
  outpostActions: QueryState.init({}),
  scanningConfig: QueryState.init({} as OnboardingConfigResponse),
  resourceDetails: {},
  resources: {},
  outpostToken: QueryState.init({}),
  enabledRegions: QueryState.init([]),
  zones: QueryState.init([]),
  subnets: QueryState.init([]),
  networks: QueryState.init([]),
  accountsScanned: {},
};

const outpostSlice = createSlice({
  name: 'outpost',
  initialState,
  reducers: {
    /*get vulnerability scanner*/
    getVulnerabilityScanners(
      state,
      action: QueryAction<Record<string, ScanVaultScannerInfo[]>>,
    ) {
      state.vulnerabilityOutposts = QueryState.next(
        state.vulnerabilityOutposts,
        action,
      );
    },

    /*get data scanners*/
    getDataScanners(
      state,
      action: QueryAction<ScannerStatusResponse, FileDataApiGetScannersRequest>,
    ) {
      state.dataOutposts = QueryState.next(state.dataOutposts, action, {
        mapData: r => r?.scanners || {},
      });
    },

    /*Ariksa scanner*/
    getAriksaOutposts(state, action: QueryAction<ScanVaultCentralAccount[]>) {
      state.ariksaOutposts = QueryState.next(state.ariksaOutposts, action);
    },

    /*get config*/
    getScanningConfig(
      state,
      action: QueryAction<
        OnboardingConfigResponse,
        CloudAccountApiReadScanningConfigRequest
      >,
    ) {
      state.scanningConfig = QueryState.next(state.scanningConfig, action);
    },

    /*get enable region for scanning*/
    enableRegionForScanning(
      state,
      action: QueryAction<
        OrgEnabledRegionsResponse,
        DefaultApiAddEnabledRegionsRequest
      >,
    ) {
      state.outpostActions = QueryState.next(state.outpostActions, action, {
        mapData: r => r.regions,
      });
    },

    /*get enabled regions for scanning*/
    getEnabledRegionsForScanning(
      state,
      action: QueryAction<
        OrgEnabledRegionsResponse,
        DefaultApiGetEnabledRegionsRequest
      >,
    ) {
      state.enabledRegions = QueryState.next(state.enabledRegions, action, {
        mapData: r => r.regions ?? [],
      });
    },

    /*replace enabled regions for scanning*/
    updateEnabledRegionsForScanning(
      state,
      action: QueryAction<
        OrgEnabledRegionsResponse,
        DefaultApiReplaceEnabledRegionsRequest
      >,
    ) {
      state.outpostActions = QueryState.next(state.outpostActions, action, {
        mapData: r => r.regions,
      });
    },

    /*get resource details*/
    getResourceDetails(
      state,
      action: QueryAction<InsightV2Pagination, InsightV2ApiInsightV2Request>,
    ) {
      const resourceType = action.payload.q.insightV2Request.resource_type;
      if (!state.resourceDetails[resourceType!]) {
        state.resourceDetails[resourceType!] = QueryState.init({} as any, {
          status: QueryStatus.pending,
        });
        return;
      } else {
        state.resourceDetails[resourceType!] = QueryState.next(
          state.resourceDetails[resourceType!],
          action,
          {
            mapData: res => {
              let data: Record<string, any> = {};
              forEach(res?.items, o => (data[o.resource_id] = o));
              return data;
            },
          },
        );
      }
    },

    generateOutpostToken(state, action: QueryAction<any>) {
      state.outpostToken = QueryState.next(state.outpostToken, action);
    },

    /*get zones*/
    getZones(
      state,
      action: QueryAction<
        string[],
        ResourceApiGetZonesByRegionForAccountRequest
      >,
    ) {
      state.zones = QueryState.next(state.zones, action);
    },

    /*rescan*/
    rescanResources(
      state,
      action: QueryAction<any, DefaultApiUpdateScannerSettingsRequest>,
    ) {
      state.outpostActions = QueryState.next(state.outpostActions, action);
    },

    /*detach account*/
    detachAccount(
      state,
      action: QueryAction<
        ScanVaultCentralAccount,
        ItemApiUpdateCentralScanningAccountStatusRequest
      >,
    ) {
      state.outpostActions = QueryState.next(state.outpostActions, action);
    },

    /*rescan*/
    getAccountsScanned(
      state,
      action: QueryAction<
        ScanVaultCrossAccountsResponse,
        DefaultApiGetCrossAccountsForScanningRequest
      >,
    ) {
      const id = action.payload.q.accountUuid;
      if (!state.accountsScanned[id]) {
        state.accountsScanned[id] = QueryState.init({} as any, {
          status: QueryStatus.pending,
        });
        return;
      } else {
        state.accountsScanned[id] = QueryState.next(
          state.accountsScanned[id],
          action,
        );
      }
    },

    /*get subnets and networks*/
    getSubnets(
      state,
      action: QueryAction<InsightV2Pagination, InsightV2ApiInsightV2Request>,
    ) {
      state.subnets = QueryState.next(state.subnets, action, {
        mapData: r => r.items,
      });
    },
    getNetworks(
      state,
      action: QueryAction<InsightV2Pagination, InsightV2ApiInsightV2Request>,
    ) {
      state.networks = QueryState.next(state.networks, action, {
        mapData: r => r.items,
      });
    },
  },
});

export const { actions, reducer, name: sliceKey } = outpostSlice;
